import { useState, useEffect } from "react";
import { useTranslations } from "next-intl";
import { observer, inject } from "mobx-react";
import axios from "axios";
import CloseOneIcon from "../svg/CloseOneIcon";
import Input from "../form-fields/Input";
import SearchableSelect from "../form-fields/SearchableSelect";
import ButtonWithLoading from "../form-fields/ButtonWithLoading";
import { fetchCities, fetchStreets } from "@/utils/locationService";

const ShippingAreaCheckModal = inject("wooCommerceStore", "studioStore")(observer(({ wooCommerceStore, studioStore }) => {
  const [formData, setFormData] = useState({
    city: "",
    street: "",
    house_number: "",
  });
  const [cities, setCities] = useState([]);
  const [streets, setStreets] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [checkResult, setCheckResult] = useState(null);
  const t = useTranslations("Account");

  const container = studioStore.settings.product_popup?.container;
  const productInfoBackgroundColor = studioStore.settings.product_popup?.product_info_background_color;
  const primaryColor = studioStore.settings.colors.primary_color;

  let classes = "fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-2xl md:mx-auto max-h-[90vh] z-50 overscroll-contain";
  const style = {
    backgroundColor: productInfoBackgroundColor,
  };

  if (container === "stamp") {
    classes = classes + " stamp-border";
    style.backgroundImage = `radial-gradient(8px, transparent 98%, ${productInfoBackgroundColor}),linear-gradient(${productInfoBackgroundColor} 0px, ${productInfoBackgroundColor} 0px)`;
    style.backgroundColor = '';
  } else {
    classes = classes + " rounded-lg shadow-lg p-4 bg-white";
  }

  useEffect(() => {
    const fetchInitialData = async () => {
      const citiesData = await fetchCities("IL");
      setCities(citiesData);
    };
    fetchInitialData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCityChange = async (e) => {
    const { value } = e.target;
    const city = cities.find(c => c.name === value);

    // Clear the street when city changes
    setFormData(prev => ({
      ...prev,
      city: value,
      street: "",
    }));

    // Immediately clear the streets list to prevent flickering
    setStreets([]);

    if (city?.external_id) {
      const data = await fetchStreets(city.external_id);
      setStreets(data);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const address = `${formData.street} ${formData.house_number}, ${formData.city}`;
      const encodedAddress = encodeURIComponent(address);
      const encodedCity = encodeURIComponent(formData?.city || "");
      const encodedStreet = encodeURIComponent(formData?.street || "");
      const encodedHouseNumber = encodeURIComponent(formData?.house_number || "");
      const response = await axios.get(
        `${process.env.NEXT_PUBLIC_WORDPRESS_SITE_URL}wp-json/beaver-next-shipping-area/v1/check-address?address=${encodedAddress}&city=${encodedCity}&street=${encodedStreet}&house_number=${encodedHouseNumber}`,
      );

      setCheckResult(response.data);
      // Mark as checked and save address if successful
      localStorage.setItem('shipping_area_checked', 'true');
      if (response.data.is_within_area) {
        localStorage.setItem('saved_address', JSON.stringify({
          city: formData.city,
          street: formData.street,
          house_number: formData.house_number,
        }));
      }
    } catch (error) {
      console.error("Error checking shipping area:", error);
      setCheckResult({ is_within_area: false });
      // Mark as checked even on error to prevent repeated errors
      localStorage.setItem('shipping_area_checked', 'true');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" data-testid="shipping-area-modal">
      <div className="fixed inset-0 transition-opacity" aria-hidden="true">
        <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
      </div>

      <div className={classes} style={style}>
        <button
          className="absolute top-4 md:-top-8 left-4 md:-left-10 rtl:right-4 rtl:md:-right-10 rtl:left-auto z-10"
          onClick={() => {
            localStorage.setItem('shipping_area_checked', 'true');
            wooCommerceStore.toggleShippingAreaCheckModal();
          }}
          data-testid="shipping-area-close-button"
        >
          <CloseOneIcon size="28px" stroke={primaryColor} />
        </button>

        <div className="max-h-[86vh] overflow-y-auto">
          <div className="p-3 md:p-6">
            <div className="px-4 md:px-12">
              <h2 className="text-2xl font-bold text-primary-studio font-secondary mb-6">
                {t("check_shipping_area")}
              </h2>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-4">
                  <SearchableSelect
                    title={t("city")}
                    name="city"
                    value={formData.city}
                    options={cities}
                    handleChange={handleCityChange}
                    placeholder={t("select_city")}
                    required
                    data-testid="shipping-city-select"
                  />

                  <SearchableSelect
                    title={t("street")}
                    name="street"
                    value={formData.street}
                    options={streets}
                    handleChange={handleChange}
                    placeholder={t("select_street")}
                    required
                    data-testid="shipping-street-select"
                  />

                  <Input
                    title={t("house_number")}
                    type="text"
                    name="house_number"
                    value={formData.house_number}
                    handleChange={handleChange}
                    required
                    data-testid="shipping-house-number"
                  />
                </div>

                {checkResult !== null && (
                  <>
                    <div className={`text-center p-4 rounded-lg ${checkResult.is_within_area ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {checkResult.is_within_area ? t("shipping_available") : t("shipping_not_available")}
                    </div>
                    {checkResult.is_within_area && (
                      <div className="mt-4">
                        <ButtonWithLoading
                          type="button"
                          isLoading={false}
                          text={t("continue_shopping")}
                          onClick={() => wooCommerceStore.toggleShippingAreaCheckModal()}
                          className="w-full"
                          data-testid="shipping-continue-button"
                        />
                      </div>
                    )}
                  </>
                )}

                <div className="mt-6">
                  <ButtonWithLoading
                    type="submit"
                    isLoading={isLoading}
                    text={t("check_availability")}
                    loadingText={t("checking")}
                    className="w-full"
                    data-testid="shipping-check-button"
                    outline={checkResult}
                  />
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}));

export default ShippingAreaCheckModal;